import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import {
  ErrorResponse,
  ValidationErrorResponse
} from "../../../requests/@types";
import api from "../../../services/api";
import { IntegrationType } from "../../samples/@types";
import { DYNAMIC_INTEGRATIONS } from "../../samples/components/reanalyze/reanalyze-form";
import { headerQueryKeys } from "../queryKeys";

export type AnalysisTypeInfo = {
  enabled?: boolean;
  additional_information?: string;
  last_completed_on?: string;
  autoupload?: boolean;
  display_status?: string;
  is_loading_analysis_status?: boolean;
  queue_filled?: boolean;
};

export type DynamicAnalysisKeys = Exclude<IntegrationType, "tc" | "ti_cloud">;
type DynamicAnalysis = Record<DynamicAnalysisKeys, AnalysisTypeInfo>;

export type AnalysisTypes = {
  tc: AnalysisTypeInfo & {
    current_version: string;
    analyzed_with_old_tc?: boolean;
  };
  ti_cloud: AnalysisTypeInfo;
  dynamic_analysis: DynamicAnalysis;
};

export const areAdditionalAnalysisTypesAvailable = (
  analysisTypes: AnalysisTypes
) =>
  Object.entries(analysisTypes.dynamic_analysis).some(
    ([analysisName, analysis]) =>
      analysis.enabled &&
      !["rl_dynamic_analysis", "rl_auxiliary_analysis"].includes(analysisName)
  );

export const getIsIntegrationEnabled = (dynamicAnalysis: DynamicAnalysis) =>
  DYNAMIC_INTEGRATIONS.some(
    integration => dynamicAnalysis?.[integration?.name]?.enabled
  );

type PrepareReanalyzeDataParams = {
  hashes?: string[];
  skipRlcs?: boolean;
  isUrl?: boolean;
};

const usePrepareReanalyzeData = ({
  hashes = [],
  skipRlcs = false,
  isUrl = false
}: PrepareReanalyzeDataParams = {}) => {
  const { data, isLoading, error, isError } = useQuery<
    AxiosResponse<AnalysisTypes>,
    ErrorResponse<ValidationErrorResponse>
  >({
    queryFn: () =>
      api.prepareReanalyze({
        hashValues: hashes,
        skipRlcs,
        isUrl,
        signal: undefined
      }),
    queryKey: headerQueryKeys.prepareReanalyze(hashes, skipRlcs, isUrl)
  });

  if (isError) {
    const defaultErrorMessage =
      "Unexpected error while preparing the Reanalyze dialog. Please try again.";
    if (error.data?.detail?.fields || error?.data?.message) {
      const { fields } = error.data?.detail ?? {};
      const detailedErrorMessage = fields
        ? fields?.hash_values?.[0] ?? defaultErrorMessage
        : error.data.message;
      show({
        level: NotificationsLevel.ERROR,
        message: detailedErrorMessage
      });
    } else {
      show({
        level: NotificationsLevel.ERROR,
        message: defaultErrorMessage
      });
    }
  }

  return {
    data: data?.data as AnalysisTypes,
    isLoading,
    error
  };
};

export default usePrepareReanalyzeData;
